import { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { ClientConfig } from '../../clientConfig';
import { getRedirectValidationTarget, getValidatedRedirectUrl } from '../../utils/redirectValidation';
import { ClientConfigContext } from '../clientConfigContext';
import { isRedirectUrlValid } from '../services/validation';

type InvalidRedirectHandler = () => void;
export type ValidateRedirectUrl = (candidate: unknown) => Promise<string | undefined>;

/**
 * Validates browser redirects against both the client allow-list and the server configuration.
 * Invalid destinations use the supplied handler or fall back to the standard error page.
 */
export function useValidatedUrl(onInvalidRedirect?: InvalidRedirectHandler): ValidateRedirectUrl {
    const history = useHistory();
    const { redirectAllowedList } = useContext<ClientConfig>(ClientConfigContext);

    return useCallback(
        async (candidate: unknown) => {
            const validatedUrl = getValidatedRedirectUrl(candidate, redirectAllowedList);
            const validationTarget = getRedirectValidationTarget(candidate, redirectAllowedList);
            if (validatedUrl && validationTarget && (await isRedirectUrlValid(validationTarget))) {
                return validatedUrl;
            }

            if (onInvalidRedirect) {
                onInvalidRedirect();
            } else {
                history.push('/error');
            }
            return undefined;
        },
        [history, onInvalidRedirect, redirectAllowedList],
    );
}
